import {
  isAfter,
  isSameDay,
  isValid,
  parseISO,
  addMonths,
  startOfMonth,
  endOfMonth,
  subMonths,
  format
} from 'date-fns';
import { apiDateFormat, defaultDateFormat } from '../constants/dateFormats';
import { enUS, fi } from 'date-fns/locale';

export function formatDate(
  date: string | number | Date,
  dateFormat: string = defaultDateFormat
): string {
  return format(new Date(date), dateFormat);
}

export const parseStringToDate = (dateString: string): Date | null => {
  return isValid(parseISO(dateString)) ? parseISO(dateString) : null;
};

export const hasDatePassed = (date: Date | null): boolean | null => {
  const today = new Date();
  return date !== null && isValid(date)
    ? isSameDay(today, date) || isAfter(today, date)
    : null;
};

export const getMonthNameForLocale = (
  locale: string,
  offsetFromCurrentMonth: number,
  date: Date | null = null
): string => {
  const startingMonth = !date ? new Date() : date;
  const targetMonth = addMonths(startingMonth, offsetFromCurrentMonth);
  const options: Intl.DateTimeFormatOptions = { month: 'long' };
  return targetMonth.toLocaleDateString(locale, options);
};

export const dateToApiFormat = (date: Date | number | null): string => {
  return date ? format(date, apiDateFormat) : '';
};

export const apiToDefaultDateFormat = (apiDate: Date | string): string => {
  const date = new Date(apiDate);
  return format(date, defaultDateFormat);
};

export const getFirstDayOfCurrentMonth = (): string => {
  const date = new Date();
  return format(startOfMonth(date), apiDateFormat);
};

export const getFirstDayOfLastMonth = (): string => {
  const date = new Date();
  const lastMonth = subMonths(date, 1);
  return format(startOfMonth(lastMonth), apiDateFormat);
};

export const getLastDayOfCurrentMonth = (): string => {
  const date = new Date();
  return format(endOfMonth(date), apiDateFormat);
};

export const getLastDayOfLastMonth = (): string => {
  const date = new Date();
  const lastMonth = subMonths(date, 1);
  return format(endOfMonth(lastMonth), apiDateFormat);
};

export const isFuture = (date: string) => {
  return isAfter(new Date(date), new Date());
};

export const monthDiff = (dateFrom: Date, dateTo: Date) => {
  return (
    dateTo.getMonth() -
    dateFrom.getMonth() +
    12 * (dateTo.getFullYear() - dateFrom.getFullYear())
  );
};

export const getStartOfDay = (date: Date) => {
  return new Date(date.setUTCHours(0, 0, 0, 0));
};

export const getEndOfDay = (date: Date) => {
  return new Date(date.setUTCHours(23, 59, 59, 999));
};

export const getFirstDayOfNextMonth = (language: string): string => {
  const date = startOfMonth(addMonths(new Date(), 1));

  if (language === 'fi') {
    return format(date, 'dd.MM.yyyy', { locale: fi });
  }

  return format(date, 'dd MMMM yyyy', { locale: enUS });
};
